import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { login } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { Button } from '@/ui/Button'
import { Input } from '@/ui/Input'

export function LoginPage() {
  const navigate = useNavigate()
  const { user, loading: authLoading, setAuthenticatedUser } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  if (!authLoading && user) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await login(email, password)
      if (data.status === 'authenticated') {
        setAuthenticatedUser(data.user)
        navigate('/', { replace: true })
      } else {
        setError('Unexpected login response')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-ink text-cream grain-overlay">
      <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_440px]">
        <section className="flex flex-col justify-between px-8 py-8 lg:px-12 border-r border-haze/50">
          <div className="flex items-center gap-3">
            <span className="h-2 w-2 rounded-full bg-ember" />
            <span className="font-mono text-xs uppercase text-mute">CRM Simulation</span>
          </div>

          <div className="max-w-2xl py-16">
            <p className="font-mono text-xs uppercase text-mute mb-5">Simulation operations workspace</p>
            <h1 className="font-display text-5xl lg:text-6xl font-medium text-cream leading-tight mb-6">
              Live reply drafts and bot replays backed by DeepSeek.
            </h1>
            <p className="text-base text-mute leading-relaxed max-w-xl">
              Review unattended CRM conversations, generate operator-ready replies, and compare batch simulations from one admin console.
            </p>
          </div>

          <p className="font-mono text-xs text-mute/70">Pydantic AI + DeepSeek</p>
        </section>

        <section className="flex items-center px-8 py-10 lg:px-10">
          <div className="w-full">
            <div className="mb-8">
              <h2 className="font-display text-2xl font-medium text-cream mb-2">Sign in</h2>
              <p className="text-sm text-mute">Use your CRM Simulation account.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-cream mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  className="text-base placeholder-mute/50"
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-cream mb-2">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  className="text-base placeholder-mute/50"
                  placeholder="Password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              {error && (
                <div className="text-sm text-ember border border-ember/40 px-3 py-2 bg-ember/5 rounded-lg">
                  {error}
                </div>
              )}

              <Button type="submit" loading={loading} size="lg" className="w-full">
                {loading ? 'Signing in' : 'Sign in'}
              </Button>
            </form>
          </div>
        </section>
      </div>
    </div>
  )
}
